/**
 * Admin.jsx - System administration page.
 *
 * Provides eight tabbed views for managing the Vendor Management System:
 *   1. Health          - System health tiles, record counts, service info
 *   2. Business rules  - Configurable scoring and tolerance parameters
 *   3. Currencies      - Exchange-rate management
 *   4. Users           - User accounts CRUD with roles and lock management
 *   5. Backup & restore - Manual and scheduled backup management
 *   6. Sessions        - Active session monitoring and forced sign-out
 *   7. Security notes  - Read-only security feature summary
 *   8. Sync History    - BC sync status, counts, and per-branch breakdown
 */

import { useState, useEffect, useCallback } from "react";
import {
  HeartPulse,
  Database,
  HardDrive,
  Users as UsersIcon,
  ShieldCheck,
  RefreshCw,
  Plus,
  Download,
  Upload,
  Trash2,
  LogOut,
  Loader2,
  Save,
  AlertTriangle,
  CheckCircle2,
  Lock,
  Unlock,
  Activity,
  Clock,
  Key,
  Server,
  FileText,
  Eye,
  EyeOff,
  GitBranch,
} from "lucide-react";
import { Tile, DataTable, Badge, Modal, Loading, useToast } from "../components";

import {
  getHealth,
  getBusinessRules,
  saveBusinessRules,
  getCurrencies,
  saveCurrency,
  deleteCurrency,
  getUsers,
  createUser,
  updateUser,
  getBackups,
  createBackup,
  exportAll,
  restoreBackup,
  getSessions,
  revokeSession,
  getSecurityNotes,
  getSyncHistory,
  getProductSyncHistory,
} from "../api/admin";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const TABS = [
  { key: "health", label: "Health", icon: HeartPulse },
  { key: "rules", label: "Business rules", icon: FileText },
  { key: "currencies", label: "Currencies", icon: HardDrive },
  { key: "users", label: "Users", icon: UsersIcon },
  { key: "backup", label: "Backup & restore", icon: Database },
  { key: "sessions", label: "Sessions", icon: Activity },
  { key: "security", label: "Security notes", icon: ShieldCheck },
  { key: "sync", label: "Sync History", icon: GitBranch },
];

const ROLES = ["admin", "manager", "analyst", "viewer"];

/* ------------------------------------------------------------------ */
/* Column definitions                                                  */
/* ------------------------------------------------------------------ */

const USER_COLUMNS = [
  { key: "username", label: "Username" },
  { key: "fullName", label: "Full Name" },
  { key: "email", label: "Email" },
  { key: "role", label: "Role", type: "badge" },
  { key: "active", label: "Active", type: "flag" },
  {
    key: "mustChangePassword",
    label: "Must Change PW",
    type: "flag",
  },
  {
    key: "lockedUntil",
    label: "Locked Until",
    type: "date",
  },
  {
    key: "lastLogin",
    label: "Last Login",
    type: "date",
  },
];

const BACKUP_COLUMNS = [
  { key: "createdAt", label: "Created", type: "date" },
  { key: "fileName", label: "File" },
  { key: "size", label: "Size", type: "num" },
  { key: "contents", label: "Contents" },
  {
    key: "_actions",
    label: "",
    sortable: false,
    render: (_, row) => row, // handled in the table wrapper
  },
];

const SESSION_COLUMNS = [
  { key: "username", label: "User" },
  { key: "role", label: "Role", type: "badge" },
  { key: "remoteAddress", label: "From" },
  { key: "signedInAt", label: "Signed In", type: "date" },
  { key: "lastSeenAt", label: "Last Seen", type: "date" },
  { key: "expiresAt", label: "Expires", type: "date" },
  {
    key: "_actions",
    label: "",
    sortable: false,
    render: (_, row) => row,
  },
];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function AdminPage() {
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState("health");
  const [initialLoading, setInitialLoading] = useState(true);

  /* Health */
  const [health, setHealth] = useState(null);

  /* Business rules */
  const [rules, setRules] = useState(null);
  const [rulesSaving, setRulesSaving] = useState(false);

  /* Currencies */
  const [currencies, setCurrencies] = useState([]);
  const [newCurrencyCode, setNewCurrencyCode] = useState("");
  const [newCurrencyRate, setNewCurrencyRate] = useState("");
  const [currencySaving, setCurrencySaving] = useState(false);

  /* Users */
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: "",
    fullName: "",
    email: "",
    role: "viewer",
    password: "",
    active: true,
    unlock: false,
  });
  const [userSaving, setUserSaving] = useState(false);
  const [showUserPw, setShowUserPw] = useState(false);

  /* Backups */
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupCreating, setBackupCreating] = useState(false);
  const [restoreConfirm, setRestoreConfirm] = useState(null);
  const [restoring, setRestoring] = useState(false);

  /* Sessions */
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);

  /* Security notes */
  const [securityNotes, setSecurityNotes] = useState(null);

  /* Sync History */
  const [syncHistory, setSyncHistory] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);

  /* Products Sync History */
  const [productSync, setProductSync] = useState(null);
  const [productSyncLoading, setProductSyncLoading] = useState(false);
  const [productPage, setProductPage] = useState(1);
  const [productCompany, setProductCompany] = useState("All");
  const [productSearch, setProductSearch] = useState("");

  /* ---------------------------------------------------------------- */
  /* Data fetching                                                     */
  /* ---------------------------------------------------------------- */

  const loadInitialData = useCallback(async () => {
    setInitialLoading(true);
    try {
      const [healthRes, rulesRes, currenciesRes, usersRes, backupsRes, sessionsRes, securityRes] =
        await Promise.all([
          getHealth().catch(() => null),
          getBusinessRules().catch(() => null),
          getCurrencies().catch(() => []),
          getUsers().catch(() => []),
          getBackups().catch(() => []),
          getSessions().catch(() => []),
          getSecurityNotes().catch(() => null),
        ]);
      setHealth(healthRes);
      setRules(rulesRes);
      setCurrencies(currenciesRes);
      setUsers(usersRes);
      setBackups(backupsRes);
      setSessions(sessionsRes);
      setSecurityNotes(securityRes);
    } catch (err) {
      console.error("Failed to load admin data:", err);
      toast("Failed to load admin data", "bad");
    } finally {
      setInitialLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  /* ---------------------------------------------------------------- */
  /* Tab change handler - lazy load data                               */
  /* ---------------------------------------------------------------- */

  const handleTabChange = useCallback(
    async (tab) => {
      setActiveTab(tab);
      // Refresh data for the selected tab
      try {
        switch (tab) {
          case "health": {
            const res = await getHealth().catch(() => null);
            setHealth(res);
            break;
          }
          case "rules": {
            const res = await getBusinessRules().catch(() => null);
            setRules(res);
            break;
          }
          case "currencies": {
            const res = await getCurrencies().catch(() => []);
            setCurrencies(res);
            break;
          }
          case "users": {
            setUsersLoading(true);
            const res = await getUsers().catch(() => []);
            setUsers(res);
            setUsersLoading(false);
            break;
          }
          case "backup": {
            setBackupsLoading(true);
            const res = await getBackups().catch(() => []);
            setBackups(res);
            setBackupsLoading(false);
            break;
          }
          case "sessions": {
            setSessionsLoading(true);
            const res = await getSessions().catch(() => []);
            setSessions(res);
            setSessionsLoading(false);
            break;
          }
          case "security": {
            const res = await getSecurityNotes().catch(() => null);
            setSecurityNotes(res);
            break;
          }
          case "sync": {
            setSyncLoading(true);
            const res = await getSyncHistory().catch(() => null);
            setSyncHistory(res);
            setSyncLoading(false);
            // Load product sync data
            setProductSyncLoading(true);
            try {
              const prodRes = await getProductSyncHistory({ page: 1, perPage: 20 });
              setProductSync(prodRes);
              setProductPage(1);
            } catch {
              setProductSync(null);
            } finally {
              setProductSyncLoading(false);
            }
            break;
          }
          default:
            break;
        }
      } catch (err) {
        console.error(`Failed to load ${tab} data:`, err);
      }
    },
    [],
  );

  /* ---------------------------------------------------------------- */
  /* Business rules handlers                                           */
  /* ---------------------------------------------------------------- */

  const handleSaveRules = useCallback(async () => {
    setRulesSaving(true);
    try {
      const updated = await saveBusinessRules(rules);
      setRules(updated);
      toast("Business rules saved", "good");
    } catch (err) {
      console.error("Failed to save rules:", err);
      toast(err.message || "Failed to save rules", "bad");
    } finally {
      setRulesSaving(false);
    }
  }, [rules, toast]);

  const handleRuleChange = useCallback((field, value) => {
    setRules((prev) => ({ ...prev, [field]: value }));
  }, []);

  /* ---------------------------------------------------------------- */
  /* Currency handlers                                                 */
  /* ---------------------------------------------------------------- */

  const handleAddCurrency = useCallback(async () => {
    if (!newCurrencyCode.trim()) {
      toast("Enter a currency code", "warn");
      return;
    }
    setCurrencySaving(true);
    try {
      await saveCurrency({
        code: newCurrencyCode.trim().toUpperCase(),
        rate: parseFloat(newCurrencyRate) || 1,
      });
      const res = await getCurrencies().catch(() => []);
      setCurrencies(res);
      setNewCurrencyCode("");
      setNewCurrencyRate("");
      toast("Currency added", "good");
    } catch (err) {
      console.error("Failed to add currency:", err);
      toast(err.message || "Failed to add currency", "bad");
    } finally {
      setCurrencySaving(false);
    }
  }, [newCurrencyCode, newCurrencyRate, toast]);

  const handleCurrencyRateChange = useCallback(
    async (code, rate) => {
      try {
        await saveCurrency({ code, rate: parseFloat(rate) || 1 });
        const res = await getCurrencies().catch(() => []);
        setCurrencies(res);
        toast(`Rate updated for ${code}`, "good");
      } catch (err) {
        console.error("Failed to update rate:", err);
        toast(err.message || "Failed to update rate", "bad");
      }
    },
    [toast],
  );

  const handleDeleteCurrency = useCallback(
    async (code) => {
      try {
        await deleteCurrency(code);
        const res = await getCurrencies().catch(() => []);
        setCurrencies(res);
        toast(`Currency ${code} removed`, "good");
      } catch (err) {
        console.error("Failed to delete currency:", err);
        toast(err.message || "Failed to delete currency", "bad");
      }
    },
    [toast],
  );

  /* ---------------------------------------------------------------- */
  /* User handlers                                                     */
  /* ---------------------------------------------------------------- */

  const openAddUser = useCallback(() => {
    setEditingUser(null);
    setShowUserPw(false);
    setUserForm({
      username: "",
      fullName: "",
      email: "",
      role: "viewer",
      password: "",
      active: true,
      unlock: false,
    });
    setUserModalOpen(true);
  }, []);

  const openEditUser = useCallback((user) => {
    setEditingUser(user);
    setShowUserPw(false);
    setUserForm({
      username: user.username || "",
      fullName: user.fullName || "",
      email: user.email || "",
      role: user.role || "viewer",
      password: "",
      active: user.active !== false,
      unlock: false,
    });
    setUserModalOpen(true);
  }, []);

  const handleSaveUser = useCallback(async () => {
    if (!userForm.username.trim()) {
      toast("Username is required", "warn");
      return;
    }
    if (!editingUser && !userForm.password) {
      toast("Password is required for new users", "warn");
      return;
    }
    setUserSaving(true);
    try {
      if (editingUser) {
        await updateUser(editingUser.username, userForm);
        toast("User updated", "good");
      } else {
        await createUser(userForm);
        toast("User created", "good");
      }
      setUserModalOpen(false);
      const res = await getUsers().catch(() => []);
      setUsers(res);
    } catch (err) {
      console.error("Failed to save user:", err);
      toast(err.message || "Failed to save user", "bad");
    } finally {
      setUserSaving(false);
    }
  }, [userForm, editingUser, toast]);

  /* ---------------------------------------------------------------- */
  /* Backup handlers                                                   */
  /* ---------------------------------------------------------------- */

  const handleCreateBackup = useCallback(async () => {
    setBackupCreating(true);
    try {
      await createBackup();
      const res = await getBackups().catch(() => []);
      setBackups(res);
      toast("Backup created", "good");
    } catch (err) {
      console.error("Failed to create backup:", err);
      toast(err.message || "Failed to create backup", "bad");
    } finally {
      setBackupCreating(false);
    }
  }, [toast]);

  const handleExportAll = useCallback(async () => {
    try {
      const blob = await exportAll();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vms-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast("Export downloaded", "good");
    } catch (err) {
      console.error("Failed to export:", err);
      toast(err.message || "Failed to export", "bad");
    }
  }, [toast]);

  const handleRestore = useCallback(
    async (backup) => {
      setRestoring(true);
      try {
        await restoreBackup(backup.fileName || backup.id);
        toast("Backup restored successfully. Reloading...", "good");
        setRestoreConfirm(null);
        // Reload all data
        setTimeout(() => loadInitialData(), 1500);
      } catch (err) {
        console.error("Failed to restore:", err);
        toast(err.message || "Failed to restore backup", "bad");
      } finally {
        setRestoring(false);
      }
    },
    [toast, loadInitialData],
  );

  /* ---------------------------------------------------------------- */
  /* Session handlers                                                  */
  /* ---------------------------------------------------------------- */

  const handleRevokeSession = useCallback(
    async (session) => {
      try {
        await revokeSession(session.id || session.sessionId);
        const res = await getSessions().catch(() => []);
        setSessions(res);
        toast(`Session for ${session.username} revoked`, "good");
      } catch (err) {
        console.error("Failed to revoke session:", err);
        toast(err.message || "Failed to revoke session", "bad");
      }
    },
    [toast],
  );

  /* ---------------------------------------------------------------- */
  /* Tab renderers                                                     */
  /* ---------------------------------------------------------------- */

  function renderHealthTab() {
    const h = health || {};
    const tiles = h.tiles || {};
    const recordCounts = h.recordCounts || {};
    const serviceInfo = h.serviceInfo || {};

    return (
      <div>
        {/* Health tiles */}
        <div style={s.tileGrid}>
          <Tile
            label="Background Worker"
            value={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                {tiles.workerStatus === "running" ? (
                  <CheckCircle2 size={16} style={{ color: "var(--good)" }} />
                ) : (
                  <AlertTriangle size={16} style={{ color: "var(--bad)" }} />
                )}
                {tiles.workerStatus || "Unknown"}
              </span>
            }
            variant={tiles.workerStatus === "running" ? "good" : "bad"}
          />
          <Tile
            label="Database Size"
            value={tiles.databaseSize || "\u2014"}
            note={tiles.databaseGrowth || undefined}
          />
          <Tile
            label="Backups"
            value={tiles.backupCount ?? "\u2014"}
            note={tiles.lastBackup ? `Last: ${new Date(tiles.lastBackup).toLocaleDateString()}` : undefined}
          />
          <Tile
            label="Active Sessions"
            value={tiles.activeSessions ?? "\u2014"}
          />
        </div>

        {/* Record counts */}
        {Object.keys(recordCounts).length > 0 && (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-head">
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
                <Database size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
                Record Counts
              </h3>
            </div>
            <div className="card-body">
              <div style={s.countGrid}>
                {Object.entries(recordCounts).map(([entity, count]) => (
                  <div key={entity} style={s.countItem}>
                    <span style={s.countLabel}>{entity}</span>
                    <span style={s.countValue}>{count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Service info */}
        {Object.keys(serviceInfo).length > 0 && (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-head">
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
                <Server size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
                Service Info
              </h3>
            </div>
            <div className="card-body">
              {Object.entries(serviceInfo).map(([key, value]) => (
                <div key={key} style={s.infoRow}>
                  <span style={s.infoLabel}>{key}</span>
                  <span>{String(value)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Last background error */}
        {h.lastBackgroundError && (
          <div className="card" style={{ marginTop: 20, borderLeft: "3px solid var(--bad)" }}>
            <div className="card-body">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <AlertTriangle size={16} style={{ color: "var(--bad)" }} />
                <strong style={{ fontSize: 13 }}>Last Background Error</strong>
              </div>
              <p style={{ fontSize: 13, color: "var(--ink-2)", margin: 0 }}>
                {h.lastBackgroundError}
              </p>
            </div>
          </div>
        )}

        {/* Last backup result */}
        {h.lastBackupResult && (
          <div
            className="card"
            style={{
              marginTop: 20,
              borderLeft: `3px solid ${h.lastBackupResult.success ? "var(--good)" : "var(--warn)"}`,
            }}
          >
            <div className="card-body">
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                {h.lastBackupResult.success ? (
                  <CheckCircle2 size={16} style={{ color: "var(--good)" }} />
                ) : (
                  <AlertTriangle size={16} style={{ color: "var(--warn)" }} />
                )}
                <strong style={{ fontSize: 13 }}>Last Backup Result</strong>
              </div>
              <p style={{ fontSize: 13, color: "var(--ink-2)", margin: 0 }}>
                {h.lastBackupResult.message || (h.lastBackupResult.success ? "Completed successfully" : "Completed with warnings")}
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderRulesTab() {
    if (!rules) {
      return (
        <div style={{ textAlign: "center", padding: 40, color: "var(--ink-3)" }}>
          <Loader2 size={24} className="spin" />
          <p style={{ marginTop: 12 }}>Loading business rules...</p>
        </div>
      );
    }

    const ruleFields = [
      { key: "baseCurrency", label: "Base Currency", type: "text" },
      { key: "onTimeGraceDays", label: "On-Time Grace Days", type: "number" },
      { key: "tooEarlyThreshold", label: "Too-Early Threshold (days)", type: "number" },
      { key: "inFullTolerance", label: "In-Full Tolerance (%)", type: "number" },
      { key: "defaultReportingPeriod", label: "Default Reporting Period", type: "select", options: ["monthly", "quarterly", "yearly"] },
      { key: "priceStalenessDays", label: "Price Staleness (days)", type: "number" },
      { key: "minSourcesPerItem", label: "Min Sources per Item", type: "number" },
    ];

    const weights = rules.defaultScorecardWeights || {};

    return (
      <div>
        <div className="card">
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <FileText size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Business Rules & Scoring Parameters
            </h3>
            <button
              className="btn"
              onClick={handleSaveRules}
              disabled={rulesSaving}
            >
              {rulesSaving ? (
                <Loader2 size={14} className="spin" />
              ) : (
                <Save size={14} />
              )}
              Save Rules
            </button>
          </div>
          <div className="card-body">
            <div style={s.rulesGrid}>
              {ruleFields.map((field) => (
                <div key={field.key} style={s.formField}>
                  <label style={s.fieldLabel}>{field.label}</label>
                  {field.type === "select" ? (
                    <select
                      value={rules[field.key] || ""}
                      onChange={(e) => handleRuleChange(field.key, e.target.value)}
                      style={s.fieldSelect}
                    >
                      {field.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt.charAt(0).toUpperCase() + opt.slice(1)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type={field.type}
                      value={rules[field.key] ?? ""}
                      onChange={(e) =>
                        handleRuleChange(
                          field.key,
                          field.type === "number"
                            ? parseFloat(e.target.value) || 0
                            : e.target.value,
                        )
                      }
                      style={s.fieldInput}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Scorecard weights */}
            <div style={{ marginTop: 24 }}>
              <h4 style={s.sectionTitle}>Default Scorecard Weights</h4>
              <p style={s.sectionHint}>
                Weights should total 100%. They determine how vendor scorecards are calculated.
              </p>
              <div style={s.rulesGrid}>
                {Object.entries(weights).map(([weightKey, weightVal]) => (
                  <div key={weightKey} style={s.formField}>
                    <label style={s.fieldLabel}>
                      {weightKey.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase())}
                    </label>
                    <input
                      type="number"
                      value={weightVal ?? 0}
                      onChange={(e) => {
                        const newWeights = {
                          ...weights,
                          [weightKey]: parseFloat(e.target.value) || 0,
                        };
                        handleRuleChange("defaultScorecardWeights", newWeights);
                      }}
                      style={s.fieldInput}
                      min="0"
                      max="100"
                    />
                  </div>
                ))}
              </div>
              {Object.values(weights).reduce((sum, v) => sum + (Number(v) || 0), 0) !== 100 && (
                <div style={s.weightWarning}>
                  <AlertTriangle size={14} />
                  <span>
                    Total: {Object.values(weights).reduce((sum, v) => sum + (Number(v) || 0), 0).toFixed(0)}%
                    {" "}(should be 100%)
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderCurrenciesTab() {
    return (
      <div>
        <div className="card">
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <HardDrive size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Exchange Rates
            </h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 16 }}>
              Rates are expressed as units of base currency per 1 unit of the
              foreign currency.
              {rules?.baseCurrency && (
                <strong> Base currency: {rules.baseCurrency}</strong>
              )}
            </p>

            {/* Existing currencies */}
            <div style={s.currencyList}>
              {currencies.map((cur) => (
                <div key={cur.code} style={s.currencyRow}>
                  <span style={s.currencyCode}>{cur.code}</span>
                  <div style={s.currencyRateInput}>
                    <input
                      type="number"
                      step="0.0001"
                      value={cur.rate ?? 1}
                      onChange={(e) => {
                        const newRate = parseFloat(e.target.value) || 1;
                        setCurrencies((prev) =>
                          prev.map((c) =>
                            c.code === cur.code ? { ...c, rate: newRate } : c,
                          ),
                        );
                      }}
                      style={s.fieldInput}
                      min="0"
                    />
                  </div>
                  <button
                    className="btn ghost"
                    onClick={() => handleCurrencyRateChange(cur.code, cur.rate)}
                    style={{ padding: "4px 10px", fontSize: 12 }}
                  >
                    <Save size={12} />
                    Update
                  </button>
                  <button
                    className="btn ghost"
                    onClick={() => handleDeleteCurrency(cur.code)}
                    style={{ padding: "4px 10px", fontSize: 12, color: "var(--bad)" }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
              {currencies.length === 0 && (
                <p style={{ color: "var(--ink-3)", fontSize: 13, textAlign: "center", padding: 16 }}>
                  No currencies configured
                </p>
              )}
            </div>

            {/* Add new currency */}
            <div style={s.addCurrencyRow}>
              <input
                type="text"
                placeholder="Code (e.g. USD)"
                value={newCurrencyCode}
                onChange={(e) => setNewCurrencyCode(e.target.value)}
                style={{ ...s.fieldInput, maxWidth: 120 }}
                maxLength={3}
              />
              <input
                type="number"
                step="0.0001"
                placeholder="Rate"
                value={newCurrencyRate}
                onChange={(e) => setNewCurrencyRate(e.target.value)}
                style={{ ...s.fieldInput, maxWidth: 140 }}
                min="0"
              />
              <button
                className="btn"
                onClick={handleAddCurrency}
                disabled={currencySaving || !newCurrencyCode.trim()}
              >
                {currencySaving ? (
                  <Loader2 size={14} className="spin" />
                ) : (
                  <Plus size={14} />
                )}
                Add Currency
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderUsersTab() {
    const userColumns = [
      ...USER_COLUMNS,
      {
        key: "_actions",
        label: "",
        sortable: false,
        render: (_, row) => (
          <button
            className="btn ghost"
            style={{ padding: "3px 10px", fontSize: 12 }}
            onClick={(e) => {
              e.stopPropagation();
              openEditUser(row);
            }}
          >
            Edit
          </button>
        ),
      },
    ];

    return (
      <div>
        <div className="card">
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <UsersIcon size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              User Accounts
            </h3>
            <button className="btn" onClick={openAddUser}>
              <Plus size={14} />
              Add User
            </button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <DataTable
              columns={userColumns}
              rows={users}
              onRow={openEditUser}
              emptyText="No users found"
            />
          </div>
        </div>

        {/* User modal */}
        {userModalOpen && (
          <Modal
            title={editingUser ? `Edit User: ${editingUser.username}` : "Add New User"}
            onClose={() => setUserModalOpen(false)}
            footer={
              <>
                <button className="btn ghost" onClick={() => setUserModalOpen(false)}>
                  Cancel
                </button>
                <button
                  className="btn"
                  onClick={handleSaveUser}
                  disabled={userSaving}
                >
                  {userSaving ? (
                    <Loader2 size={14} className="spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  {editingUser ? "Save Changes" : "Create User"}
                </button>
              </>
            }
          >
            <div style={s.userForm}>
              <div style={s.formField}>
                <label style={s.fieldLabel}>Username</label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) =>
                    setUserForm((f) => ({ ...f, username: e.target.value }))
                  }
                  style={s.fieldInput}
                  disabled={!!editingUser}
                  placeholder="e.g. jsmith"
                />
              </div>
              <div style={s.formField}>
                <label style={s.fieldLabel}>Full Name</label>
                <input
                  type="text"
                  value={userForm.fullName}
                  onChange={(e) =>
                    setUserForm((f) => ({ ...f, fullName: e.target.value }))
                  }
                  style={s.fieldInput}
                  placeholder="e.g. John Smith"
                />
              </div>
              <div style={s.formField}>
                <label style={s.fieldLabel}>Email</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) =>
                    setUserForm((f) => ({ ...f, email: e.target.value }))
                  }
                  style={s.fieldInput}
                  placeholder="e.g. john@company.com"
                />
              </div>
              <div style={s.formField}>
                <label style={s.fieldLabel}>Role</label>
                <select
                  value={userForm.role}
                  onChange={(e) =>
                    setUserForm((f) => ({ ...f, role: e.target.value }))
                  }
                  style={s.fieldSelect}
                >
                  {ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={s.formField}>
                <label style={s.fieldLabel}>
                  {editingUser ? "New Password (leave blank to keep)" : "Password"}
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showUserPw ? "text" : "password"}
                    value={userForm.password}
                    onChange={(e) =>
                      setUserForm((f) => ({ ...f, password: e.target.value }))
                    }
                    style={{ ...s.fieldInput, paddingRight: 40 }}
                    placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
                  />
                  <button
                    type="button"
                    onClick={() => setShowUserPw((v) => !v)}
                    tabIndex={-1}
                    style={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      color: "var(--ink-3)",
                      cursor: "pointer",
                      padding: 4,
                      display: "flex",
                      alignItems: "center",
                    }}
                    aria-label={showUserPw ? "Hide password" : "Show password"}
                  >
                    {showUserPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div style={s.checkboxRow}>
                <label style={s.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={userForm.active}
                    onChange={(e) =>
                      setUserForm((f) => ({ ...f, active: e.target.checked }))
                    }
                  />
                  Active
                </label>
              </div>
              {editingUser && (
                <div style={s.checkboxRow}>
                  <label style={s.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={userForm.unlock}
                      onChange={(e) =>
                        setUserForm((f) => ({ ...f, unlock: e.target.checked }))
                      }
                    />
                    Unlock account
                  </label>
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>
    );
  }

  function renderBackupTab() {
    const backupColumns = [
      { key: "createdAt", label: "Created", type: "date" },
      { key: "fileName", label: "File" },
      { key: "size", label: "Size", type: "num" },
      { key: "contents", label: "Contents" },
      {
        key: "_actions",
        label: "",
        sortable: false,
        render: (_, row) => (
          <button
            className="btn ghost"
            style={{
              padding: "3px 10px",
              fontSize: 12,
              color: "var(--warn)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              setRestoreConfirm(row);
            }}
          >
            <RefreshCw size={12} />
            Restore
          </button>
        ),
      },
    ];

    return (
      <div>
        <div className="card">
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <Database size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Backup & Restore
            </h3>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                className="btn ghost"
                onClick={handleExportAll}
              >
                <Download size={14} />
                Export All
              </button>
              <button
                className="btn"
                onClick={handleCreateBackup}
                disabled={backupCreating}
              >
                {backupCreating ? (
                  <Loader2 size={14} className="spin" />
                ) : (
                  <Upload size={14} />
                )}
                Back Up Now
              </button>
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <DataTable
              columns={backupColumns}
              rows={backups}
              emptyText="No backups yet"
            />
          </div>
        </div>

        {/* Restore confirmation modal */}
        {restoreConfirm && (
          <Modal
            title="Confirm Restore"
            onClose={() => setRestoreConfirm(null)}
            narrow
            footer={
              <>
                <button
                  className="btn ghost"
                  onClick={() => setRestoreConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn danger"
                  onClick={() => handleRestore(restoreConfirm)}
                  disabled={restoring}
                >
                  {restoring ? (
                    <Loader2 size={14} className="spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Restore Backup
                </button>
              </>
            }
          >
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <AlertTriangle
                size={24}
                style={{ color: "var(--warn)", flexShrink: 0, marginTop: 2 }}
              />
              <div>
                <p style={{ fontSize: 13, fontWeight: 600, margin: "0 0 8px" }}>
                  This will overwrite current data with the backup from:
                </p>
                <p style={{ fontSize: 13, color: "var(--ink-2)", margin: 0 }}>
                  <strong>{restoreConfirm.fileName}</strong>
                  {restoreConfirm.createdAt && (
                    <>
                      <br />
                      Created:{" "}
                      {new Date(restoreConfirm.createdAt).toLocaleString()}
                    </>
                  )}
                </p>
                <p style={{ fontSize: 12, color: "var(--bad)", marginTop: 12, marginBottom: 0 }}>
                  This action cannot be undone. All current data will be replaced.
                </p>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  function renderSessionsTab() {
    const sessionColumns = [
      { key: "username", label: "User" },
      { key: "role", label: "Role", type: "badge" },
      { key: "remoteAddress", label: "From" },
      { key: "signedInAt", label: "Signed In", type: "date" },
      { key: "lastSeenAt", label: "Last Seen", type: "date" },
      { key: "expiresAt", label: "Expires", type: "date" },
      {
        key: "_actions",
        label: "",
        sortable: false,
        render: (_, row) => (
          <button
            className="btn ghost"
            style={{
              padding: "3px 10px",
              fontSize: 12,
              color: "var(--bad)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleRevokeSession(row);
            }}
          >
            <LogOut size={12} />
            Sign Out
          </button>
        ),
      },
    ];

    return (
      <div className="card">
        <div className="card-head">
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
            <Activity size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
            Active Sessions
          </h3>
          <button
            className="btn ghost"
            onClick={async () => {
              setSessionsLoading(true);
              try {
                const res = await getSessions().catch(() => []);
                setSessions(res);
              } finally {
                setSessionsLoading(false);
              }
            }}
            disabled={sessionsLoading}
          >
            {sessionsLoading ? (
              <Loader2 size={14} className="spin" />
            ) : (
              <RefreshCw size={14} />
            )}
            Refresh
          </button>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <DataTable
            columns={sessionColumns}
            rows={sessions}
            emptyText="No active sessions"
          />
        </div>
      </div>
    );
  }

  function renderSecurityTab() {
    const notes = securityNotes || {};
    const features = notes.features || [];
    const recommendations = notes.recommendations || [];

    return (
      <div>
        <div className="card">
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <ShieldCheck size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Security Features
            </h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: 13, color: "var(--ink-2)", marginBottom: 16 }}>
              The following security measures are active in this system.
              This information is read-only.
            </p>

            {features.length > 0 ? (
              <div style={s.securityList}>
                {features.map((feature, i) => (
                  <div key={i} style={s.securityItem}>
                    <CheckCircle2
                      size={16}
                      style={{ color: "var(--good)", flexShrink: 0, marginTop: 2 }}
                    />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>
                        {feature.name || feature.title || feature}
                      </div>
                      {(feature.description || feature.details) && (
                        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                          {feature.description || feature.details}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ color: "var(--ink-3)", fontSize: 13 }}>
                No security feature details available.
              </p>
            )}
          </div>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-head">
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
                <Key size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
                Recommendations
              </h3>
            </div>
            <div className="card-body">
              <div style={s.securityList}>
                {recommendations.map((rec, i) => {
                  const isImplemented = rec.status === "implemented";
                  return (
                    <div key={i} style={s.securityItem}>
                      {isImplemented ? (
                        <CheckCircle2
                          size={16}
                          style={{ color: "var(--good)", flexShrink: 0, marginTop: 2 }}
                        />
                      ) : (
                        <AlertTriangle
                          size={16}
                          style={{ color: "var(--warn)", flexShrink: 0, marginTop: 2 }}
                        />
                      )}
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <span style={{ fontSize: 13, fontWeight: 600 }}>
                            {rec.name || rec.title || rec}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              padding: "2px 6px",
                              borderRadius: "var(--radius-sm)",
                              background: isImplemented ? "var(--good-soft)" : "var(--warn-soft)",
                              color: isImplemented ? "var(--good)" : "var(--warn)",
                            }}
                          >
                            {isImplemented ? "✓ Active" : "Optional"}
                          </span>
                        </div>
                        {(rec.description || rec.details) && (
                          <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
                            {rec.description || rec.details}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- */
  /* Sync History tab                                                  */
  /* ---------------------------------------------------------------- */

  function renderSyncTab() {
    if (syncLoading) {
      return (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Loader2 size={28} className="spin" style={{ color: "var(--brand)" }} />
          <p style={{ marginTop: 12, color: "var(--ink-3)", fontSize: 13 }}>
            Loading sync history...
          </p>
        </div>
      );
    }

    if (!syncHistory) {
      return (
        <div style={{ textAlign: "center", padding: 60, color: "var(--ink-3)" }}>
          <GitBranch size={32} style={{ opacity: 0.4, marginBottom: 12 }} />
          <p>Could not load sync history.</p>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleTabChange("sync")}
            style={{ marginTop: 12 }}
          >
            <RefreshCw size={14} style={{ marginRight: 6 }} />
            Retry
          </button>
        </div>
      );
    }

    const { status, counts, branches, vendorsByCompany } = syncHistory;
    const syncStatus = status?.status || "idle";
    const syncModule = status?.module || null;
    const syncMessage = status?.message || null;
    const syncUpdatedAt = status?.updated_at || null;

    /* Status badge colour */
    const statusColor =
      syncStatus === "running"
        ? "var(--brand)"
        : syncStatus === "completed" || syncStatus === "idle"
        ? "var(--good)"
        : syncStatus === "failed"
        ? "var(--danger)"
        : "var(--ink-3)";

    /* Count items to display */
    const countItems = counts
      ? [
          { label: "Vendors Inserted", value: counts.vendorsInserted ?? 0, color: "var(--good)" },
          { label: "Vendors Updated", value: counts.vendorsUpdated ?? 0, color: "var(--brand)" },
          { label: "Products Inserted", value: counts.productsInserted ?? 0, color: "var(--good)" },
          { label: "Products Updated", value: counts.productsUpdated ?? 0, color: "var(--brand)" },
          { label: "Orders Inserted", value: counts.ordersInserted ?? 0, color: "var(--good)" },
          { label: "Orders Updated", value: counts.ordersUpdated ?? 0, color: "var(--brand)" },
          { label: "Lines Inserted", value: counts.linesInserted ?? 0, color: "var(--good)" },
          { label: "Lines Updated", value: counts.linesUpdated ?? 0, color: "var(--brand)" },
        ]
      : [];

    return (
      <div>
        {/* Current Status Card */}
        <div className="card">
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <GitBranch size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Sync Status
            </h3>
            <button
              className="btn btn-sm btn-outline"
              onClick={() => handleTabChange("sync")}
              disabled={syncLoading}
            >
              {syncLoading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
              <span style={{ marginLeft: 6 }}>Refresh</span>
            </button>
          </div>
          <div className="card-body">
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
              {/* Status indicator */}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: statusColor,
                    boxShadow: syncStatus === "running" ? `0 0 8px ${statusColor}` : "none",
                    animation: syncStatus === "running" ? "pulse 1.5s infinite" : "none",
                  }}
                />
                <span style={{ fontSize: 15, fontWeight: 600, textTransform: "capitalize" }}>
                  {syncStatus}
                </span>
              </div>

              {/* Module */}
              {syncModule && (
                <div style={{ fontSize: 13, color: "var(--ink-2)" }}>
                  Module: <strong>{syncModule}</strong>
                </div>
              )}

              {/* Last updated */}
              {syncUpdatedAt && (
                <div style={{ fontSize: 13, color: "var(--ink-3)" }}>
                  <Clock size={13} style={{ verticalAlign: "text-bottom", marginRight: 4 }} />
                  {new Date(syncUpdatedAt).toLocaleString()}
                </div>
              )}
            </div>

            {/* Message */}
            {syncMessage && (
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 12px",
                  borderRadius: 6,
                  backgroundColor:
                    syncStatus === "failed"
                      ? "rgba(239,68,68,0.08)"
                      : "rgba(0,0,0,0.03)",
                  fontSize: 13,
                  color: syncStatus === "failed" ? "var(--danger)" : "var(--ink-2)",
                  border:
                    syncStatus === "failed"
                      ? "1px solid rgba(239,68,68,0.2)"
                      : "1px solid rgba(0,0,0,0.06)",
                }}
              >
                {syncMessage}
              </div>
            )}
          </div>
        </div>

        {/* Sync Counts Grid */}
        {countItems.length > 0 && (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-head">
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
                <Activity size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
                Last Sync Counts
              </h3>
            </div>
            <div className="card-body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
                  gap: 12,
                }}
              >
                {countItems.map((item) => (
                  <div
                    key={item.label}
                    style={{
                      padding: "14px 16px",
                      borderRadius: 8,
                      backgroundColor: "var(--bg)",
                      border: "1px solid var(--border)",
                    }}
                  >
                    <div style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                      {item.label}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: item.color, marginTop: 4 }}>
                      {item.value.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Per-Branch Breakdown */}
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <Database size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Per-Branch Breakdown
            </h3>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={thStyle}>Company</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Total POs</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>With BC ID</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>BC ID %</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>With Lines</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Lines %</th>
                  </tr>
                </thead>
                <tbody>
                  {branches && branches.length > 0 ? (
                    branches.map((b) => (
                      <tr key={b.company} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={tdStyle}>{b.company}</td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                          {Number(b.total_pos).toLocaleString()}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {Number(b.with_bc_id).toLocaleString()}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          <Badge
                            text={`${b.bcIdPct}%`}
                            variant={b.bcIdPct >= 90 ? "good" : b.bcIdPct >= 50 ? "warn" : "bad"}
                          />
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {Number(b.with_lines).toLocaleString()}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          <Badge
                            text={`${b.linesPct}%`}
                            variant={b.linesPct >= 90 ? "good" : b.linesPct >= 50 ? "warn" : "bad"}
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ ...tdStyle, textAlign: "center", color: "var(--ink-3)" }}>
                        No branch data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Vendors by Company */}
        {vendorsByCompany && vendorsByCompany.length > 0 && (
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-head">
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
                <UsersIcon size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
                Vendors by Company
              </h3>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={thStyle}>Company</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Vendors</th>
                  </tr>
                </thead>
                <tbody>
                  {vendorsByCompany.map((v) => (
                    <tr key={v.company_name} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={tdStyle}>{v.company_name}</td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                        {Number(v.cnt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Products Sync History */}
        {renderProductsSyncSection()}
      </div>
    );
  }


  /* ---------------------------------------------------------------- */
  /* Products Sync History section                                     */
  /* ---------------------------------------------------------------- */

  function renderProductsSyncSection() {
    if (productSyncLoading) {
      return (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <Database size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Sync Products History
            </h3>
          </div>
          <div className="card-body" style={{ textAlign: "center", padding: 40 }}>
            <Loader2 size={24} className="spin" style={{ color: "var(--brand)" }} />
            <p style={{ marginTop: 12, color: "var(--ink-3)", fontSize: 13 }}>Loading products...</p>
          </div>
        </div>
      );
    }

    if (!productSync) {
      return null;
    }

    const { summary, pagination, products } = productSync;

    const loadProductPage = async (pg) => {
      setProductSyncLoading(true);
      try {
        const res = await getProductSyncHistory({
          page: pg,
          perPage: 20,
          company: productCompany,
          search: productSearch || undefined,
        });
        setProductSync(res);
        setProductPage(pg);
      } catch {
        toast("Failed to load products", "bad");
      } finally {
        setProductSyncLoading(false);
      }
    };

    const handleCompanyFilter = (val) => {
      setProductCompany(val);
      setProductPage(1);
      setProductSyncLoading(true);
      getProductSyncHistory({ page: 1, perPage: 20, company: val, search: productSearch || undefined })
        .then((res) => setProductSync(res))
        .catch(() => setProductSync(null))
        .finally(() => setProductSyncLoading(false));
    };

    const handleSearch = (val) => {
      setProductSearch(val);
    };

    const doSearch = () => {
      setProductPage(1);
      setProductSyncLoading(true);
      getProductSyncHistory({ page: 1, perPage: 20, company: productCompany, search: productSearch || undefined })
        .then((res) => setProductSync(res))
        .catch(() => setProductSync(null))
        .finally(() => setProductSyncLoading(false));
    };

    return (
      <div>
        {/* Summary Cards */}
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-head">
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 650 }}>
              <Database size={16} style={{ verticalAlign: "text-bottom", marginRight: 8 }} />
              Sync Products History
            </h3>
          </div>
          <div className="card-body">
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{
                padding: "12px 20px", borderRadius: 8, background: "var(--bg)",
                border: "1px solid var(--border)", minWidth: 160,
              }}>
                <div style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Total Products
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: "var(--good)", marginTop: 4 }}>
                  {summary.totalProducts.toLocaleString()}
                </div>
              </div>
              {summary.companies.map((c) => (
                <div key={c.name} style={{
                  padding: "12px 20px", borderRadius: 8, background: "var(--bg)",
                  border: "1px solid var(--border)", minWidth: 160,
                }}>
                  <div style={{ fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.5 }}>
                    {c.name}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--brand)", marginTop: 4 }}>
                    {c.count.toLocaleString()}
                  </div>
                  {c.lastSynced && (
                    <div style={{ fontSize: 11, color: "var(--ink-3)", marginTop: 4 }}>
                      Last: {new Date(c.lastSynced).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Filters */}
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 16 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase" }}>Company</label>
                <select
                  value={productCompany}
                  onChange={(e) => handleCompanyFilter(e.target.value)}
                  style={{ ...s.fieldInput, minWidth: 200 }}
                >
                  <option value="All">All Companies</option>
                  {summary.companies.map((c) => (
                    <option key={c.name} value={c.name}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 200 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--ink-3)", textTransform: "uppercase" }}>Search</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    placeholder="Item no or description..."
                    value={productSearch}
                    onChange={(e) => handleSearch(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch()}
                    style={{ ...s.fieldInput, flex: 1 }}
                  />
                  <button className="btn" onClick={doSearch} style={{ padding: "8px 16px", fontSize: 13 }}>
                    Search
                  </button>
                </div>
              </div>
            </div>

            {/* Products Table */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "2px solid var(--border)" }}>
                    <th style={thStyle}>Item No</th>
                    <th style={thStyle}>Description</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Unit Cost</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Unit Price</th>
                    <th style={thStyle}>UoM</th>
                    <th style={thStyle}>Category</th>
                    <th style={thStyle}>Companies</th>
                    <th style={{ ...thStyle, textAlign: "right" }}>Last Synced</th>
                  </tr>
                </thead>
                <tbody>
                  {products && products.length > 0 ? (
                    products.map((p) => (
                      <tr key={p.id} style={{ borderBottom: "1px solid var(--border)" }}>
                        <td style={{ ...tdStyle, fontWeight: 600, fontFamily: "monospace", fontSize: 12 }}>
                          {p.itemNo}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.description}>
                            {p.description}
                          </div>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {p.unitCost > 0 ? p.unitCost.toFixed(2) : "-"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right" }}>
                          {p.unitPrice > 0 ? p.unitPrice.toFixed(2) : "-"}
                        </td>
                        <td style={tdStyle}>{p.baseUom || "-"}</td>
                        <td style={tdStyle}>{p.categoryCode || "-"}</td>
                        <td style={{ ...tdStyle, fontSize: 11, color: "var(--ink-2)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.companies}>
                          {p.companies || "-"}
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontSize: 12, color: "var(--ink-3)" }}>
                          {p.updatedAt ? new Date(p.updatedAt).toLocaleDateString() : "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: "var(--ink-3)", padding: 24 }}>
                        {productSyncLoading ? "Loading..." : "No products found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, paddingTop: 12, borderTop: "1px solid var(--border)" }}>
                <span style={{ fontSize: 12, color: "var(--ink-3)" }}>
                  Showing {(pagination.page - 1) * pagination.perPage + 1} - {Math.min(pagination.page * pagination.perPage, pagination.total)} of {pagination.total.toLocaleString()} products
                </span>
                <div style={{ display: "flex", gap: 6 }}>
                  <button
                    className="btn ghost"
                    disabled={pagination.page <= 1 || productSyncLoading}
                    onClick={() => loadProductPage(pagination.page - 1)}
                    style={{ padding: "4px 12px", fontSize: 12 }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 12, color: "var(--ink-2)", padding: "4px 8px" }}>
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <button
                    className="btn ghost"
                    disabled={pagination.page >= pagination.totalPages || productSyncLoading}
                    onClick={() => loadProductPage(pagination.page + 1)}
                    style={{ padding: "4px 12px", fontSize: 12 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* Table cell styles for sync tab */
  const thStyle = {
    padding: "10px 16px",
    textAlign: "left",
    fontWeight: 600,
    fontSize: 12,
    color: "var(--ink-3)",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  };
  const tdStyle = {
    padding: "10px 16px",
  };

  /* ---------------------------------------------------------------- */
  /* Main render                                                       */
  /* ---------------------------------------------------------------- */

  const TAB_RENDERERS = {
    health: renderHealthTab,
    rules: renderRulesTab,
    currencies: renderCurrenciesTab,
    users: renderUsersTab,
    backup: renderBackupTab,
    sessions: renderSessionsTab,
    security: renderSecurityTab,
    sync: renderSyncTab,
  };

  return (
    <>
      <Loading show={initialLoading} text="Loading administration data..." />

      {/* Tab bar */}
      <div style={s.tabBar}>
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              style={{
                ...s.tab,
                ...(isActive ? s.tabActive : {}),
              }}
            >
              <Icon size={15} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div style={{ marginTop: 20 }}>
        {TAB_RENDERERS[activeTab]?.()}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const s = {
  tabBar: {
    display: "flex",
    gap: 4,
    overflowX: "auto",
    borderBottom: "1px solid var(--line)",
    paddingBottom: 0,
  },
  tab: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink-3)",
    background: "none",
    border: "none",
    borderBottom: "2px solid transparent",
    cursor: "pointer",
    whiteSpace: "nowrap",
    transition: "color 0.12s, border-color 0.12s",
    flexShrink: 0,
  },
  tabActive: {
    color: "var(--accent)",
    borderBottom: "2px solid var(--accent)",
    fontWeight: 650,
  },
  tileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gap: 16,
  },
  countGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
    gap: 12,
  },
  countItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "8px 12px",
    background: "var(--bg)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
  },
  countLabel: {
    fontWeight: 550,
    color: "var(--ink-2)",
    textTransform: "capitalize",
  },
  countValue: {
    fontWeight: 700,
    color: "var(--ink)",
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "5px 0",
    fontSize: 13,
  },
  infoLabel: {
    fontWeight: 600,
    color: "var(--ink-2)",
    minWidth: 160,
    flexShrink: 0,
  },
  rulesGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 16,
  },
  formField: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: "var(--ink-2)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  fieldInput: {
    fontSize: 13,
    padding: "8px 10px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--line)",
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
  },
  fieldSelect: {
    fontSize: 13,
    padding: "8px 10px",
    borderRadius: "var(--radius-sm)",
    border: "1px solid var(--line)",
    background: "var(--panel)",
    color: "var(--ink)",
    outline: "none",
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: 650,
    margin: "0 0 6px 0",
    color: "var(--ink)",
  },
  sectionHint: {
    fontSize: 12,
    color: "var(--ink-3)",
    margin: "0 0 12px 0",
  },
  weightWarning: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 12,
    padding: "8px 12px",
    background: "var(--warn-soft)",
    borderRadius: "var(--radius-sm)",
    fontSize: 13,
    fontWeight: 550,
    color: "var(--warn)",
  },
  currencyList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  currencyRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 12px",
    background: "var(--bg)",
    borderRadius: "var(--radius-sm)",
  },
  currencyCode: {
    fontWeight: 700,
    fontSize: 14,
    fontFamily: "monospace",
    minWidth: 50,
    color: "var(--ink)",
  },
  currencyRateInput: {
    flex: 1,
    maxWidth: 180,
  },
  addCurrencyRow: {
    display: "flex",
    gap: 12,
    alignItems: "flex-end",
    marginTop: 16,
    paddingTop: 16,
    borderTop: "1px solid var(--line-2)",
  },
  userForm: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
  },
  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: 550,
    color: "var(--ink-2)",
    cursor: "pointer",
  },
  securityList: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  securityItem: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
  },
};
